import { useState } from "react";
import { useParams, Navigate } from "react-router-dom";
import Nav from "@/components/site/Nav";
import Footer from "@/components/site/Footer";
import SectionBadge from "@/components/site/SectionBadge";
import BookConsultationDialog from "@/components/site/BookConsultationDialog";
import JoinUsDialog from "@/components/site/JoinUsDialog";
import FloatingActions from "@/components/site/FloatingActions";
import EditorialHero from "@/components/site/v2/EditorialHero";
import ChallengeCards from "@/components/site/v2/ChallengeCards";
import ServiceExplorer from "@/components/site/v2/ServiceExplorer";
import FrameworkDiagram from "@/components/site/v2/FrameworkDiagram";
import OutcomeStrip from "@/components/site/v2/OutcomeStrip";
import FaqSection from "@/components/site/v2/FaqSection";
import RelatedLinks from "@/components/site/v2/RelatedLinks";
import ContextualCTA from "@/components/site/v2/ContextualCTA";
import ImageBand from "@/components/site/v2/ImageBand";
import { INDUSTRIES } from "@/data/industries";
import { INDUSTRY_V2, INDUSTRY_FRAMEWORKS } from "@/data/v2Content";
import { industryChallengeImage, industryBandImage, industryImagePool, imageForPath, uniquePicker, ALL_IMAGES } from "@/data/images";
import { INDUSTRY_SEO } from "@/data/seoMap";
import RelatedInsights from "@/components/site/v2/RelatedInsights";
import { INDUSTRY_INSIGHTS } from "@/data/insightsMap";
import LeadMagnetSection from "@/components/site/v2/LeadMagnetSection";
import LeadMagnetDialog from "@/components/site/LeadMagnetDialog";
import { INDUSTRY_LEAD_MAGNET } from "@/data/leadMagnets";
import IndustryTools from "@/components/tools/IndustryTools";
import { track } from "@/lib/analytics";
import { usePageMeta } from "@/lib/usePageMeta";

const GRID = "max-w-7xl mx-auto px-6 sm:px-8 md:px-12";

/**
 * V2 industry detail template — all 8 industry pages.
 * Industry hero → challenges → expandable solutions → industry framework →
 * opportunities → outcome groups → (segments/strategies) → FAQ → related → CTA.
 */
export default function IndustryDetailPage() {
  const { slug } = useParams();
  const [bookOpen, setBookOpen] = useState(false);
  const [joinOpen, setJoinOpen] = useState(false);
  const [magnetOpen, setMagnetOpen] = useState(false);

  const page = INDUSTRIES.find((i) => i.slug === slug);

  usePageMeta({
    title: page ? (INDUSTRY_SEO[slug]?.metaTitle || page.metaTitle) : "Industries | BLEZZINGS",
    description: page?.metaDescription || "Industry-specific growth consulting from BLEZZINGS.",
    canonical: page ? `https://blezzings.co/industries/${page.slug}` : undefined,
    breadcrumbs: page
      ? [
          { name: "Home", url: "https://blezzings.co/" },
          { name: "Industries", url: `https://blezzings.co/industries/${page.slug}` },
          { name: page.name, url: `https://blezzings.co/industries/${page.slug}` },
        ]
      : undefined,
    faqs: page?.faqs,
  });

  if (!page) return <Navigate to="/" replace />;

  const openBook = (source) => {
    track("book_consultation_open", { source });
    track("service_cta_click", {
      service: page.name,
      source_page: window.location.pathname,
      cta_location: source,
    });
    setBookOpen(true);
  };

  // Curated content for every industry; generic mapping as fallback.
  const v2 = INDUSTRY_V2[slug];

  // No image repeats on the page: hero, band and the 4 challenge photos are
  // reserved up front; every other card image is picked uniquely.
  const pick = uniquePicker(
    [page.image, industryBandImage(slug), ...[0, 1, 2, 3].map((i) => industryChallengeImage(slug, i))],
    [industryImagePool(slug), ALL_IMAGES]
  );

  const challenges = (
    v2?.challenges ||
    page.challenges.map((c) => ({ title: c.title, body: c.body }))
  ).map((c, i) => ({ ...c, image: industryChallengeImage(slug, i) }));

  const solutions = (
    v2?.solutions ||
    page.help.map((h) => ({
      name: h.name,
      outcome: h.body.split(". ")[0] + ".",
      body: h.body,
    }))
  ).map((s) => ({ ...s, image: s.link ? pick(imageForPath(s.link.to)) : s.image }));

  const framework = v2?.framework || INDUSTRY_FRAMEWORKS[slug];

  return (
    <div data-testid="industry-page" className="relative">
      <Nav onBook={() => openBook("nav")} />

      <EditorialHero
        testid="industry-hero"
        eyebrow={page.eyebrow}
        title={page.heroTitle}
        description={page.heroParas[0]}
        image={page.image}
        imageAlt={page.imageAlt}
        primaryLabel={INDUSTRY_SEO[slug]?.heroCta || "Book a Consultation"}
        onPrimary={() => openBook("industry-hero")}
        secondaryLabel="Explore the Solutions"
        secondaryTo="#solutions"
      />

      <ChallengeCards
        testid="industry-challenges"
        badge="The Commercial Reality"
        headline={page.industryLead}
        challenges={challenges}
        kicker={page.industryClosing}
      />

      <div id="solutions">
        <ServiceExplorer
          testid="industry-solutions"
          badge="How We Help"
          headline={page.helpLead}
          intro="Open any intervention area to see what it covers."
          items={solutions}
          deliverablesLabel="What we typically deliver"
          deliverables={page.deliverables}
        />
      </div>

      {framework && (
        <FrameworkDiagram
          testid="industry-framework"
          badge="How Growth Flows"
          headline={page.oppIntro ? "Where growth is won — and lost." : "The commercial system, connected."}
          steps={framework}
        />
      )}

      <ImageBand
        testid="industry-band"
        image={industryBandImage(slug)}
        alt={page.imageAlt || page.name}
        caption={page.name}
        position="center 30%"
      />

      {INDUSTRY_LEAD_MAGNET[slug] && (
        <LeadMagnetSection
          testid="industry-leadmagnet"
          resource={INDUSTRY_LEAD_MAGNET[slug]}
          onOpen={() => setMagnetOpen(true)}
        />
      )}

      <IndustryTools pageUrl={`/industries/${slug}`} testid="industry-tools" />

      <OutcomeStrip
        testid="industry-opportunities"
        badge="Growth Opportunities"
        headline={page.oppLead}
        items={page.opportunities}
        note={page.oppClosing}
      />

      {/* Outcome groups */}
      <section data-testid="industry-outcomes" className="py-16 md:py-20 bg-white">
        <div className={GRID}>
          <SectionBadge label="Commercial Outcomes" />
          <h2 className="font-serif-display text-3xl sm:text-4xl mt-6 leading-[1.1] max-w-2xl">
            The measures that matter.
          </h2>
          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
            {page.outcomes.map((g, i) => (
              <div
                key={g.group}
                data-testid={`industry-outcome-group-${i}`}
                className="border border-zinc-200 bg-white p-7"
              >
                <div className="text-[11px] font-semibold tracking-[0.24em] uppercase text-zinc-500">
                  {g.group}
                </div>
                <div className="mt-4 flex flex-wrap gap-2">
                  {g.items.slice(0, 3).map((item) => (
                    <span
                      key={item}
                      className="inline-flex items-center h-7 px-3 border border-black/10 bg-[#F2F2F2]/60 text-xs text-zinc-800"
                    >
                      {item}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
          {page.outcomesClosing && (
            <p className="mt-8 text-sm text-zinc-500 leading-relaxed max-w-3xl">
              {page.outcomesClosing}
            </p>
          )}
          {page.who && (
            <p className="mt-4 text-sm text-zinc-500 leading-relaxed max-w-3xl">
              {page.who}
            </p>
          )}
          {page.complianceNote && (
            <p className="mt-4 text-xs text-zinc-400 leading-relaxed max-w-3xl">
              {page.complianceNote}
            </p>
          )}
        </div>
      </section>

      {/* Professional-services style segments */}
      {page.segments && (
        <section data-testid="industry-segments" className="py-16 md:py-20 bg-[#F2F2F2]">
          <div className={GRID}>
            <SectionBadge label="Who We Serve" />
            <h2 className="font-serif-display text-3xl sm:text-4xl mt-6 leading-[1.1] max-w-2xl">
              Built for your kind of firm.
            </h2>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
              {page.segments.map((s, i) => (
                <div
                  key={s.title}
                  data-testid={`industry-segment-${i}`}
                  className="bg-white border border-zinc-200 p-7"
                >
                  <h3 className="font-serif-display text-xl leading-snug text-black">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm text-zinc-600 leading-relaxed">{s.body}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {page.strategies && (
        <ServiceExplorer
          testid="industry-strategies"
          badge="Growth Strategies"
          headline="Strategies by segment."
          items={page.strategies.map((s) => ({
            name: s.title,
            outcome: s.lead,
            body: s.intro,
          }))}
          tone="light"
        />
      )}

      <RelatedLinks
        testid="industry-related"
        badge="Related Capabilities"
        headline="The capabilities behind the work."
        links={page.related.map((r) => ({ ...r, image: pick(imageForPath(r.to)) }))}
      />

      <FaqSection testid="industry-faqs" faqs={page.faqs} />

      <RelatedInsights testid="industry-insights" slugs={INDUSTRY_INSIGHTS[slug]} />

      <ContextualCTA
        testid="industry-cta"
        heading={page.ctaHeading}
        body={page.ctaBody}
        label={page.ctaLabel}
        onBook={() => openBook("industry-cta")}
      />

      <Footer onJoin={() => setJoinOpen(true)} />
      <FloatingActions onBook={() => openBook("floating")} />
      <BookConsultationDialog open={bookOpen} onOpenChange={setBookOpen} />
      <LeadMagnetDialog open={magnetOpen} onOpenChange={setMagnetOpen} resource={INDUSTRY_LEAD_MAGNET[slug]} />
      <JoinUsDialog open={joinOpen} onOpenChange={setJoinOpen} />
    </div>
  );
}
